import React, { useState, useEffect } from 'react';
import { Container, Paper, Box, FormControl, InputLabel, Select, MenuItem, Typography, Chip, alpha, useTheme } from '@mui/material';
import {
  Scheduler,
  DayView,
  WeekView,
  MonthView,
  Toolbar,
  DateNavigator,
  ViewSwitcher,
  Appointments,
  AppointmentTooltip,
  AppointmentForm,
  DragDropProvider,
  TodayButton,
} from '@devexpress/dx-react-scheduler-material-ui';
import { ViewState, EditingState, IntegratedEditing } from '@devexpress/dx-react-scheduler';
import { useSnackbar } from 'notistack';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5002';

// Custom appointment component with Apple-style colors and dark-mode tweaks
const Appointment = ({ children, style, data, ...restProps }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const eventTypeColors = {
    shoot: isDark ? '#30D158' : '#34C759',
    meeting: isDark ? '#0A84FF' : '#007AFF',
    rehearsal: isDark ? '#BF5AF2' : '#AF52DE',
    deadline: isDark ? '#FF453A' : '#FF3B30',
    preparation: isDark ? '#FF9F0A' : '#FF9500',
    review: isDark ? '#64D2FF' : '#5AC8FA',
    other: isDark ? '#98989D' : '#8E8E93'
  };
  const color = eventTypeColors[data.eventType] || eventTypeColors.other;
  const isMonthView = style && style.height < 30;

  const formatTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const startTime = formatTime(data.startDate);

  return (
    <Appointments.Appointment
      {...restProps}
      style={{
        ...style,
        backgroundColor: color,
        borderRadius: isMonthView ? '4px' : '6px',
        border: 'none',
        boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 2px rgba(0,0,0,0.1)',
        color: '#fff',
        fontSize: isMonthView ? '11px' : '12px',
        fontWeight: 500,
        padding: isMonthView ? '2px 4px' : '4px 8px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: isDark ? '0 3px 8px rgba(0,0,0,0.4)' : '0 3px 6px rgba(0,0,0,0.15)',
          zIndex: 100
        }
      }}
    >
      {!isMonthView && data.allDay && <span style={{ fontWeight: 600 }}>{data.title}</span>}
      {!isMonthView && !data.allDay && (
        <>
          <span style={{ opacity: 0.9, fontSize: '11px' }}>{startTime}</span>
          <br />
          <span style={{ fontWeight: 600 }}>{data.title}</span>
        </>
      )}
      {isMonthView && data.title}
    </Appointments.Appointment>
  );
};

// Custom form for adding/editing appointments
const AppointmentFormComponent = ({ onFieldChange, appointmentData, ...restProps }) => {
  const handleEventTypeChange = (event) => {
    onFieldChange({ eventType: event.target.value });
  };

  return (
    <AppointmentForm.BasicLayout
      appointmentData={appointmentData}
      onFieldChange={onFieldChange}
      {...restProps}
    >
      <AppointmentForm.Label text="Event Type" />
      <AppointmentForm.Select
        value={appointmentData.eventType || 'other'}
        availableOptions={[
          { id: 'shoot', text: 'Shoot' },
          { id: 'meeting', text: 'Meeting' },
          { id: 'rehearsal', text: 'Rehearsal' },
          { id: 'deadline', text: 'Deadline' },
          { id: 'other', text: 'Other' },
        ]}
        onValueChange={handleEventTypeChange}
      />
    </AppointmentForm.BasicLayout>
  );
};

function Calendar() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentViewName, setCurrentViewName] = useState('Month');
  const [appointments, setAppointments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  // Fetch projects
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/projects`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setProjects(data);
          if (data.length > 0) {
            setSelectedProject(data[0]._id);
          }
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
        enqueueSnackbar('Failed to load projects', { variant: 'error' });
      }
    };
    fetchProjects();
  }, [enqueueSnackbar]);

  // Fetch events when selected project changes
  useEffect(() => {
    const fetchEvents = async () => {
      if (!selectedProject) return;
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/events/project/${selectedProject}`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setAppointments(data.map(event => ({
            id: event._id,
            title: event.title,
            startDate: new Date(event.startDate),
            endDate: new Date(event.endDate),
            eventType: event.eventType,
            notes: event.notes,
            location: event.location,
            description: event.description,
            participants: event.participants
          })));
        }
      } catch (error) {
        console.error('Error fetching events:', error);
        enqueueSnackbar('Failed to load events', { variant: 'error' });
      }
    };
    fetchEvents();
  }, [selectedProject, enqueueSnackbar]);

  // Handle appointment changes
  const commitChanges = async ({ added, changed, deleted }) => {
    try {
      if (added) {
        const eventData = {
          ...added,
          projectId: selectedProject,
        };
        
        const response = await fetch(`${API_BASE_URL}/api/v1/events`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(eventData)
        });
        
        if (response.ok) {
          const newEvent = await response.json();
          setAppointments([...appointments, {
            id: newEvent._id,
            title: newEvent.title,
            startDate: new Date(newEvent.startDate),
            endDate: new Date(newEvent.endDate),
            eventType: newEvent.eventType,
            notes: newEvent.notes,
            location: newEvent.location
          }]);
          enqueueSnackbar('Event added successfully', { variant: 'success' });
        }
      }
      
      if (changed) {
        const changedAppointment = appointments.find(appointment => changed[appointment.id]);
        if (changedAppointment) {
          const response = await fetch(`${API_BASE_URL}/api/v1/events/${changedAppointment.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ ...changedAppointment, ...changed[changedAppointment.id] })
          });
          
          if (response.ok) {
            setAppointments(appointments.map(appointment => (
              changed[appointment.id] ? { ...appointment, ...changed[appointment.id] } : appointment
            )));
            enqueueSnackbar('Event updated successfully', { variant: 'success' });
          }
        }
      }
      
      if (deleted !== undefined) {
        const response = await fetch(`${API_BASE_URL}/api/v1/events/${deleted}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        
        if (response.ok) {
          setAppointments(appointments.filter(appointment => appointment.id !== deleted));
          enqueueSnackbar('Event deleted successfully', { variant: 'success' });
        }
      }
    } catch (error) {
      console.error('Error managing event:', error);
      enqueueSnackbar('Failed to manage event', { variant: 'error' });
    }
  };

  return (
    <Container maxWidth={false} disableGutters sx={{ px: 2, py: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          mt: 3,
          mb: 2,
          borderRadius: 0,
          backgroundColor: theme.palette.background.default,
          color: theme.palette.mode === 'dark' ? 'white' : theme.palette.text.primary,
          position: 'relative',
          overflow: 'hidden',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
          <Box>
            <Typography variant="overline" sx={{ color: theme.palette.primary.main, fontWeight: 700, letterSpacing: 3, fontSize: '0.7rem', mb: 0.5, display: 'block' }}>
              SCHEDULING
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.5rem', md: '2rem' }, letterSpacing: '-0.02em', color: theme.palette.text.primary }}>
              Project Calendar
            </Typography>
            <Typography variant="body1" sx={{ maxWidth: 600, color: theme.palette.text.secondary }}>
              Manage events, shoots, and meetings across your projects.
            </Typography>
          </Box>
          <FormControl fullWidth sx={{ minWidth: 220, maxWidth: 320 }}>
            <InputLabel>Select Project</InputLabel>
            <Select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              label="Select Project"
            >
              {projects.map((project) => (
                <MenuItem key={project._id} value={project._id}>
                  {project.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>
      
      <Paper sx={{ 
        p: 2, 
        borderRadius: 2, 
        border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.2 : 0.1)}`, 
        backgroundColor: isDark ? alpha(theme.palette.background.paper, 0.95) : alpha(theme.palette.background.paper, 0.7), 
        boxShadow: isDark ? `0 8px 32px 0 ${alpha(theme.palette.common.black, 0.25)}` : `0 8px 24px 0 ${alpha(theme.palette.common.black, 0.08)}`,
        '& .Cell-cell': {
          borderColor: alpha(theme.palette.divider, isDark ? 0.05 : 0.25),
          borderWidth: '1px',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.light, isDark ? 0.06 : 0.05)
          },
          transition: 'all 0.2s ease-in-out'
        }
      }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`, pb: 1 }}>
          {[{ id: 'shoot', text: 'Shoot', color: isDark ? '#30D158' : '#34C759' },
            { id: 'meeting', text: 'Meeting', color: isDark ? '#0A84FF' : '#007AFF' },
            { id: 'rehearsal', text: 'Rehearsal', color: isDark ? '#BF5AF2' : '#AF52DE' },
            { id: 'deadline', text: 'Deadline', color: isDark ? '#FF453A' : '#FF3B30' },
            { id: 'preparation', text: 'Prep', color: isDark ? '#FF9F0A' : '#FF9500' },
            { id: 'review', text: 'Review', color: isDark ? '#64D2FF' : '#5AC8FA' },
            { id: 'other', text: 'Other', color: isDark ? '#98989D' : '#8E8E93' }
          ].map(type => (
            <Chip
              key={type.id}
              label={type.text}
              size="small"
              sx={{ backgroundColor: type.color, color: '#fff', fontWeight: 500, fontSize: '12px' }}
            />
          ))}
        </Box>

        <Box height="700px">
          <Scheduler data={appointments}>
            <ViewState
              currentDate={currentDate}
              currentViewName={currentViewName}
              onCurrentDateChange={setCurrentDate}
              onCurrentViewNameChange={setCurrentViewName}
            />
            <EditingState onCommitChanges={commitChanges} />
            <IntegratedEditing />

            <DayView startDayHour={8} endDayHour={20} />
            <WeekView startDayHour={8} endDayHour={20} />
            <MonthView />

            <Toolbar />
            <DateNavigator />
            <TodayButton />
            <ViewSwitcher />

            <Appointments appointmentComponent={Appointment} />
            <AppointmentTooltip
              showCloseButton
              showOpenButton
              showDeleteButton
            />
            <AppointmentForm basicLayoutComponent={AppointmentFormComponent} />
            <DragDropProvider />
          </Scheduler>
        </Box>
      </Paper>
    </Container>
  );
}

export default Calendar;
